import { useState } from 'react';
import { BookOpenCheck, Crown, Infinity as InfinityIcon, Lightbulb, Users } from 'lucide-react';
import BottomSheet from '../Components/BottomSheet';
import NameItCoin from '../Components/NameItCoin';
import useCoinStore from '../stores/coinStore';
import { PRODUCTS } from '../Services/products';
import { purchase } from './vatarClient';
import { claimPurchase } from './purchases';

// The Vatar build's only checkout: the Pro pass and coin packs, paid in Vatar's
// own sheet through Vatar.purchase (Developer Agreement 5.1). Product ids must
// match the products listed in the Vatar dashboard, and prices must match
// PRODUCTS (the server refuses a mismatch).
const PRO = { id: 'pro_pass', title: 'NameIt Pro', note: '31 days', price: PRODUCTS.pro_onetime.priceNgn };
const PERKS = [
  { icon: Users, text: '10 players in every room (free rooms hold 5)' },
  { icon: InfinityIcon, text: 'Host as many rooms as you like' },
  { icon: Lightbulb, text: 'Free Memory Jogger hints every round' },
  { icon: BookOpenCheck, text: 'Unlimited dictionary checks' },
];
const PACKS = [
  { id: 'coins_100', coins: 100, price: PRODUCTS.coins_100.priceNgn },
  { id: 'coins_280', coins: 280, price: PRODUCTS.coins_280.priceNgn },
  { id: 'coins_500', coins: 500, price: PRODUCTS.coins_500.priceNgn, badge: 'Popular' },
  { id: 'coins_1000', coins: 1000, price: PRODUCTS.coins_1000.priceNgn, badge: 'Best value' },
];

const naira = (n) => `₦${n.toLocaleString()}`;

function describeFailure(err) {
  switch (err?.code) {
    case 'cancelled':
      return null;
    case 'busy':
    case 'rate_limited':
      return 'One moment, then try again.';
    case 'payment_failed':
      return 'The payment did not go through. You were not charged.';
    case 'not_for_sale':
    case 'not_available':
      return 'This is not available right now.';
    case 'owned':
      return 'You already have this.';
    default:
      // Paid, but our server could not add it yet. The game claims it again next time.
      return "We got your payment but couldn't add it yet. It will be added when you next open the game.";
  }
}

export default function VatarShop({ isOpen, onClose }) {
  const coinBalance = useCoinStore((s) => s.coinBalance);
  const isPro = useCoinStore((s) => s.isPro);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);

  async function buy(id, label) {
    if (busyId) return;
    setBusyId(id);
    setMessage(null);
    try {
      const { purchaseId } = await purchase(id);
      await claimPurchase(purchaseId);
      setMessage({ ok: true, text: `${label} added!` });
    } catch (err) {
      const text = describeFailure(err);
      if (text) setMessage({ ok: false, text });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Coins & Pro" subtitle={`You have ${coinBalance ?? 0} coins`}>
      <div className="space-y-4 px-4 pb-6">
        <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 p-4">
          <div className="flex items-center gap-2">
            <Crown size={18} className="text-amber-500" />
            <span className="font-bold text-gray-900">{PRO.title}</span>
            <span className="text-xs text-amber-900/70">{PRO.note}{isPro ? ' · active' : ''}</span>
          </div>
          <ul className="mt-3 space-y-1.5">
            {PERKS.map((perk) => (
              <li key={perk.text} className="flex items-start gap-2 text-sm text-gray-700">
                <perk.icon size={15} className="mt-0.5 flex-shrink-0 text-amber-600" />
                {perk.text}
              </li>
            ))}
          </ul>
          <button
            onClick={() => buy(PRO.id, 'Pro')}
            disabled={Boolean(busyId)}
            style={{ '--press-lip': '#b45309' }}
            className="btn-press mt-4 w-full rounded-xl bg-amber-500 py-3 font-bold text-white disabled:opacity-60"
          >
            {busyId === PRO.id ? 'Opening…' : `${isPro ? 'Add 31 more days' : 'Get Pro'} · ${naira(PRO.price)}`}
          </button>
        </div>

        <div>
          <p className="mb-1 text-sm font-bold text-gray-800">Coins</p>
          <p className="mb-2 text-xs text-gray-500">Spend them on hints, AI answer review, extra categories and extra rooms.</p>
          <div className="grid grid-cols-2 gap-2">
            {PACKS.map((pack) => (
              <button
                key={pack.id}
                onClick={() => buy(pack.id, `${pack.coins.toLocaleString()} coins`)}
                disabled={Boolean(busyId)}
                className="relative rounded-2xl border border-orange-100 bg-white px-3 py-3 text-center shadow-sm disabled:opacity-60"
              >
                {pack.badge && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white">
                    {pack.badge}
                  </span>
                )}
                <span className="flex items-center justify-center gap-1.5 text-lg font-black text-gray-900">
                  <NameItCoin size={18} />
                  {pack.coins.toLocaleString()}
                </span>
                <span className="mt-0.5 block text-sm font-semibold text-red-700">{busyId === pack.id ? '…' : naira(pack.price)}</span>
              </button>
            ))}
          </div>
        </div>

        {message && <p className={`text-center text-sm ${message.ok ? 'text-green-700' : 'text-red-700'}`}>{message.text}</p>}
      </div>
    </BottomSheet>
  );
}
